import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Big from 'big.js';
import moment from 'moment';

import {
    useGetCapitalCallsQuery,
    useGetClosingsQuery,
    useGetInvestmentCharacteristicsQuery,
    useGetInvestmentFundsQuery,
} from 'api/main';
import {
    billStatus as closingStatus,
    capitalCallStatus,
    subscriptionStatus,
} from 'utils/constants';
import { parseSum } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import AddCapitalCall from './AddCapitalCall';
import CapitalCallRow from './CapitalCallRow';
import {
    CustomButton,
    CustomSelect,
    DataTile,
    EmptyTableBody,
    ListContainer,
    Loader,
    StyledTableRow,
} from 'components';
import {
    Box,
    FormHelperText,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    tile: {
        minHeight: 160,
        backgroundColor: theme.palette.grey[100],
        padding: '10px 15px',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-evenly',
        flexDirection: 'column',

        '&$center': {
            alignItems: 'center',
        },
    },
    center: {},
    select: {
        maxWidth: 470,
        minWidth: 370,
    },
}));

const renderOption = fund => (
    <MenuItem key={fund.id} value={fund.id}>
        {fund.name}
    </MenuItem>
);

const tableHeads = [
    texts.EXIGIBILITY_DATE,
    texts.PERCENTAGE,
    texts.MONTANT_APPELE,
    texts.MONTANT_SOUSCRIT,
    texts.FUND_CALLS_REASON,
    texts.FUND_CALLS_PREMIUM,
];

const FirstInstallmentRow = ({ fund, total }) => {
    const totalInvestedAmount = new Big(total)
        .times(fund.firstInstalmentPercentage)
        .div(100)
        .toNumber();

    return (
        <StyledTableRow key="first-installment">
            <TableCell align="center">-</TableCell>
            <TableCell align="center">{fund.firstInstalmentPercentage} %</TableCell>
            <TableCell align="center">{parseSum(totalInvestedAmount)}</TableCell>
            <TableCell align="center">-</TableCell>
            <TableCell align="center">{texts.FIRST_INSTALLMENT}</TableCell>
            <TableCell align="center"></TableCell>
        </StyledTableRow>
    );
};

const CapitalCalls = () => {
    const classes = useStyles();

    const [selectedFundId, setSelectedFundId] = useState('');
    const [newCallOpen, setNewCallOpen] = useState(false);
    const [amountError, setAmountError] = useState('');

    const {
        data: fundList = [],
        isFetching: fundsLoader,
        isSuccess: fundsLoaded,
    } = useGetInvestmentFundsQuery({
        'shareCategories.shareSubcategories.immediatePayment': 0,
        visible: 1,
    });

    const { data: capitalCalls = [], isLoading: listLoader } = useGetCapitalCallsQuery(
        { investmentFund: selectedFundId },
        { skip: !selectedFundId }
    );
    const { data: closings = [], isLoading: closingsLoader } = useGetClosingsQuery({
        pagination: false,
    });
    const minActivationDate = useMemo(() => {
        let minActivationDate;
        if (selectedFundId) {
            minActivationDate = closings.reduce((maxDate, c) => {
                if (
                    c.investmentFund.id === selectedFundId &&
                    (c.status === closingStatus.COMPLETED || c.status === closingStatus.ACTIVATED)
                ) {
                    return maxDate?.isSameOrAfter?.(c.issueDate, 'd')
                        ? maxDate
                        : moment(c.issueDate);
                } else {
                    return maxDate;
                }
            }, null);
        }
        if (!minActivationDate || moment(minActivationDate).isSameOrBefore(moment(), 'd')) {
            minActivationDate = moment();
        }

        return minActivationDate;
    }, [selectedFundId, closings]);

    const selectedFund = useMemo(
        () => fundList.find(f => f.id === selectedFundId),
        [selectedFundId, fundList]
    );
    const { data: characteristics = [], isFetching: characteristicsLoader } =
        useGetInvestmentCharacteristicsQuery(
            {
                'shareSubcategory.shareCategory.investmentFund': selectedFundId,
                'shareSubcategory.immediatePayment': 0,
                'subscription.subscriptionStatus': subscriptionStatus.FINISHED,
                pagination: false,
            },
            { skip: !selectedFundId }
        );
    const totalInvestedAmount = useMemo(
        () => characteristics.reduce((acc, call) => acc + call.investingSum, 0),
        [characteristics]
    );
    const totalCalledAmount = useMemo(
        () =>
            capitalCalls
                .filter(
                    capitalCall =>
                        capitalCall.status === capitalCallStatus.ACTIVATED ||
                        capitalCall.status === capitalCallStatus.COMPLETED
                )
                .reduce((acc, call) => acc + call.issueSum, 0) +
            ((selectedFund?.firstInstalmentPercentage || 0) * totalInvestedAmount) / 100,
        [capitalCalls, selectedFund, totalInvestedAmount]
    );
    const totalCalledPercentage = useMemo(
        () =>
            capitalCalls.reduce((acc, call) => acc + +call.percentage, 0) +
            selectedFund?.firstInstalmentPercentage,
        [capitalCalls, selectedFund]
    );

    const dataIsLoading = useMemo(
        () => characteristicsLoader || fundsLoader || listLoader,
        [characteristicsLoader, fundsLoader, listLoader]
    );
    const canAdd = useMemo(
        () =>
            totalInvestedAmount !== 0 &&
            totalCalledPercentage < 100 &&
            (capitalCalls.length === 0 ||
                capitalCalls.every(
                    call =>
                        call.status === capitalCallStatus.ACTIVATED ||
                        call.status === capitalCallStatus.COMPLETED
                )),
        [capitalCalls, totalInvestedAmount, totalCalledPercentage]
    );

    useEffect(() => {
        if (fundsLoaded) {
            setSelectedFundId(fundList[0].id);
        }
    }, [fundList, fundsLoaded]);

    return (
        <ListContainer>
            <Typography variant="h5" align="left" gutterBottom>
                {texts.FUND_CALLS}
            </Typography>
            <Loader loading={fundsLoader}>
                <Box display="flex" justifyContent="center">
                    <CustomSelect
                        value={selectedFundId}
                        className={classes.select}
                        onChange={e => setSelectedFundId(e.target.value)}
                    >
                        {fundList.map(renderOption)}
                    </CustomSelect>
                </Box>
            </Loader>
            <DataTile className={classes.tile + ' center'}>
                <Loader loading={!selectedFund || dataIsLoading}>
                    <React.Fragment>
                        <Typography variant="h4" color="primary" align="center" gutterBottom>
                            {selectedFund?.name}
                        </Typography>
                        <Typography align="center" variant="h5" gutterBottom>
                            {texts.MONTANT_TOTAL_SOUSCRIT} {parseSum(totalInvestedAmount)}
                        </Typography>
                        <Typography align="center" variant="h5" gutterBottom>
                            {texts.MONTANT_TOTAL_APPELE} {parseSum(totalCalledAmount)}
                        </Typography>
                    </React.Fragment>
                </Loader>
            </DataTile>
            <DataTile className={classes.tile}>
                <Typography variant="h5" align="center" color="primary" gutterBottom>
                    {texts.FUND_CALLS}
                </Typography>
                <Box display="flex" justifyContent="center">
                    <TableContainer>
                        <Table>
                            <colgroup>
                                <col width="15%" />
                                <col width="8%" />
                                <col width="17%" />
                                <col width="15%" />
                                <col width="20%" />
                                <col width="20%" />
                                <col width="5%" />
                            </colgroup>
                            <TableHead>
                                <TableRow>
                                    {tableHeads.map(th => (
                                        <TableCell align="center" key={th}>
                                            {th}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            {listLoader || capitalCalls.length === 0 ? (
                                <EmptyTableBody colSpan={tableHeads.length} loading={listLoader} />
                            ) : (
                                <TableBody>
                                    {selectedFund?.firstInstalmentPercentage ? (
                                        <FirstInstallmentRow
                                            fund={selectedFund}
                                            total={totalInvestedAmount}
                                        />
                                    ) : null}
                                    {capitalCalls.map((capitalCall, index, array) => (
                                        <CapitalCallRow
                                            key={capitalCall.id}
                                            capitalCall={capitalCall}
                                            selectedFund={selectedFund}
                                            index={index}
                                            array={array}
                                            amountError={amountError}
                                            setAmountError={setAmountError}
                                            total={totalInvestedAmount}
                                            totalCalledPercentage={totalCalledPercentage}
                                            minActivationDate={minActivationDate}
                                        />
                                    ))}
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                </Box>
                {amountError && (
                    <Box textAlign="center">
                        <FormHelperText error={!!amountError}>{amountError}</FormHelperText>
                    </Box>
                )}
                {canAdd && (
                    <Box textAlign="center" mt={3}>
                        <CustomButton
                            onClick={() => setNewCallOpen(true)}
                            label={texts.ADD_CAPITAL_CALL}
                        />
                    </Box>
                )}
                {!closingsLoader && (
                    <AddCapitalCall
                        open={newCallOpen}
                        handleClose={() => setNewCallOpen(false)}
                        totalInvested={totalInvestedAmount}
                        selectedFundId={selectedFundId}
                        totalCalledPercentage={totalCalledPercentage}
                        closings={closings}
                    />
                )}
            </DataTile>
        </ListContainer>
    );
};

FirstInstallmentRow.propTypes = {
    fund: PropTypes.object,
    total: PropTypes.number,
};

export default CapitalCalls;
